(function () {
  // Shared canvas for image brightness sampling — reused across all cards
  const _samplerCanvas = document.createElement('canvas');
  _samplerCanvas.width = 40;
  _samplerCanvas.height = 20;
  const _samplerCtx = _samplerCanvas.getContext('2d');

  // Shared cart fetch — one request per page load, invalidated after mutations
  let cartDataPromise = null;

  function fetchCartData() {
    if (!cartDataPromise) {
      cartDataPromise = fetch(window.FoxTheme.routes.cart_url, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
    }
    return cartDataPromise;
  }

  function invalidateCartCache() {
    cartDataPromise = null;
  }

  // /cart/update.js sets ABSOLUTE quantities keyed by variant id, and works
  // whether or not the line already exists. That makes it idempotent, which is
  // the whole point: the previous implementation used cart/add for the first
  // write, and cart/add is ADDITIVE. Combined with AbortController it produced
  // a runaway — abort() cancels reading the response in the browser but does
  // NOT cancel the request on the server, so an aborted cart/add still added
  // its quantity while _confirmedQty stayed at 0. The next sync therefore took
  // the cart/add branch again and added on top. Rapid taps on a slow connection
  // walked the line up 3 → 6 → 9 until Shopify clamped at max stock.
  // With absolute quantities, a duplicated or superseded request can only
  // rewrite the same number.
  function cartUpdateUrl() {
    const routes = window.FoxTheme.routes || {};
    return routes.cart_update_url || `${routes.cart_url}/update.js`;
  }

  const CART_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    // Tell Zapiet not to reset its selected delivery/pickup state.
    // Without this magic string Zapiet wipes the cart-page UI on cart mutations.
    'X-Theme-Keep-Zapiet-State': '_ZapietId',
  };

  // Kept short on purpose: the tooltip sits inside a product card and longer
  // strings overflow it. Describes the limit rather than absence — the customer
  // still has the item in their cart, they just cannot take more of it.
  const QTY_LIMIT_MESSAGE = 'Limită stoc atinsă';

  // Use Shopify's error description when present; otherwise fall back.
  // Filters out the technical "cart/<route> failed: <status>" string our
  // own code throws when the response body has no usable description.
  function friendlyErrorMessage(description) {
    if (!description || typeof description !== 'string') return QTY_LIMIT_MESSAGE;
    const trimmed = description.trim();
    if (!trimmed) return QTY_LIMIT_MESSAGE;
    if (/^cart\/(add|change|update)\b/i.test(trimmed)) return QTY_LIMIT_MESSAGE;
    if (/^\d{3}\b/.test(trimmed)) return QTY_LIMIT_MESSAGE;
    return trimmed;
  }

  if (!customElements.get('pcard-atc')) {
    class PcardAtc extends HTMLElement {
      connectedCallback() {
        this.variantId = this.dataset.variantId;
        this.qty = 0;
        this._confirmedQty = 0; // last qty the server acknowledged
        this._syncTimer = null;
        this._syncController = null;
        this._syncInFlight = false;
        this._syncPending = false;

        this.atcBtn = this.querySelector('.pcard-atc-btn');
        this.qtySelector = this.querySelector('.pcard-qty-selector');
        this.qtyMinus = this.querySelector('.pcard-qty-minus');
        this.qtyPlus = this.querySelector('.pcard-qty-plus');
        this.qtyValue = this.querySelector('.pcard-qty-value');
        this.iconMinus = this.querySelector('.pcard-icon-minus');
        this.iconTrash = this.querySelector('.pcard-icon-trash');

        this.atcBtn.addEventListener('click', this.handleAtcClick);
        this.qtyMinus.addEventListener('click', this.handleMinus);
        this.qtyPlus.addEventListener('click', this.handlePlus);

        if (this.classList.contains('pcard-atc--mobile')) {
          this._detectImageBrightness();
        }

        // On the product page, subscribe to variant changes
        if (this.dataset.sectionId) {
          this._variantUnsub = window.FoxTheme.pubsub.subscribe(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this._onVariantChange
          );
        }

        // Stay in sync when the cart is mutated elsewhere (quickview, drawer, etc.)
        this._cartUnsub = window.FoxTheme.pubsub.subscribe(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this._onCartUpdate
        );

        this.loadCartState();
      }

      disconnectedCallback() {
        if (this._variantUnsub) this._variantUnsub();
        if (this._cartUnsub) this._cartUnsub();
      }

      _onCartUpdate = (event) => {
        // Skip if a local optimistic sync is pending or in flight — our own state is authoritative
        if (this._syncTimer || this._syncInFlight || this._syncPending) return;
        const cart = event && event.cart;
        if (!cart || !Array.isArray(cart.items)) return;

        const item = cart.items.find(
          (i) => String(i.variant_id) === String(this.variantId)
        );
        const newQty = item ? item.quantity : 0;
        if (newQty === this._confirmedQty) return;

        this.qty = newQty;
        this._confirmedQty = newQty;
        this.classList.add('pcard-atc--instant');
        if (newQty > 0) {
          this.updateQtyDisplay();
          this.updateLeftIcon();
          this.showQtySelector();
        } else {
          this.showAtcBtn();
        }
        requestAnimationFrame(() =>
          requestAnimationFrame(() => this.classList.remove('pcard-atc--instant'))
        );
      };

      loadCartState = async () => {
        try {
          const cart = await fetchCartData();
          const item = cart.items.find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          if (item && item.quantity > 0) {
            this.qty = item.quantity;
            this._confirmedQty = item.quantity;
            this.updateQtyDisplay();
            this.updateLeftIcon();
            this.classList.add('pcard-atc--instant');
            this.showQtySelector();
            requestAnimationFrame(() =>
              requestAnimationFrame(() => this.classList.remove('pcard-atc--instant'))
            );
          }
        } catch (err) {
          console.error(err);
        }
      };

      // Optimistic: update UI immediately, sync to server in background
      handleAtcClick = () => {
        if (this.atcBtn.disabled) return;
        this.qty = 1;
        this.updateQtyDisplay();
        this.updateLeftIcon();
        this.showQtySelector();
        this._scheduleSync();
      };

      handleMinus = () => {
        this.qty -= 1;
        if (this.qty <= 0) {
          this.qty = 0;
          this.showAtcBtn();
        } else {
          this.updateQtyDisplay();
          this.updateLeftIcon();
        }
        this._scheduleSync();
      };

      handlePlus = () => {
        this.qty += 1;
        this.updateQtyDisplay();
        this.updateLeftIcon();
        this._scheduleSync();
      };

      _onVariantChange = (event) => {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        const variant = event.data.variant;

        // Cancel any pending sync
        clearTimeout(this._syncTimer);
        this._syncTimer = null;
        this._syncPending = false;
        if (this._syncController) this._syncController.abort();

        // Switch to new variant
        this.variantId = variant ? String(variant.id) : '';
        this.qty = 0;
        this._confirmedQty = 0;

        // Update button state for availability
        const available = variant && variant.available;
        this.atcBtn.disabled = !available;
        this.atcBtn.querySelector('.btn__text').textContent = available
          ? window.FoxTheme.variantStrings.addToCart
          : window.FoxTheme.variantStrings.soldOut;

        // Always return to the ATC button view on variant switch
        this.classList.add('pcard-atc--instant');
        this.showAtcBtn();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => this.classList.remove('pcard-atc--instant'))
        );

        // Check if new variant is already in cart
        if (available) this.loadCartState();
      };

      // Debounce: rapid taps collapse into a single request
      _scheduleSync = () => {
        clearTimeout(this._syncTimer);
        this._syncTimer = setTimeout(() => {
          this._syncTimer = null;
          this._syncToServer();
        }, 200);
      };

      _syncToServer = async () => {
        // Serialise rather than abort. A request that is aborted client-side has
        // usually already reached Shopify, so cancelling it only hides the
        // result — it does not undo it. If one is in flight, queue a follow-up
        // pass and let it run once this settles; the last pass always sends the
        // current quantity, so nothing is lost.
        if (this._syncInFlight) {
          this._syncPending = true;
          return;
        }
        this._syncInFlight = true;
        this._syncPending = false;

        this._syncController = new AbortController();
        const { signal } = this._syncController;

        const targetQty = this.qty;
        const previousConfirmed = this._confirmedQty;

        try {
          const response = await fetch(cartUpdateUrl(), {
            method: 'POST',
            headers: CART_HEADERS,
            body: JSON.stringify({ updates: { [this.variantId]: targetQty } }),
            signal,
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.status) {
            const err = new Error(data.description || `cart/update failed: ${response.status}`);
            err.serverRejected = true;
            throw err;
          }

          // cart/update returns the whole cart. Trust what came back rather than
          // what we asked for — Shopify may clamp to available stock silently
          // instead of erroring, depending on the inventory settings.
          const line = (data.items || []).find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          const serverQty = line ? line.quantity : 0;
          this._confirmedQty = serverQty;

          if (serverQty !== targetQty) {
            this.qty = serverQty;
            if (serverQty > 0) {
              this.updateQtyDisplay();
              this.updateLeftIcon();
            } else {
              this.showAtcBtn();
            }
            this._showErrorTooltip(QTY_LIMIT_MESSAGE);
          }

          invalidateCartCache();
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: data,
          });
        } catch (err) {
          if (err.name === 'AbortError') return; // variant switched — ignore
          console.error(err);
          if (err.serverRejected) {
            // Server refused (max inventory, qty rule). Show inline tooltip
            // and resync every pcard-atc for this variant to authoritative state.
            this._showErrorTooltip(friendlyErrorMessage(err.message));
            await this._resyncFromCart();
          } else {
            // Network/transport error — roll back UI to last known good state
            this.qty = previousConfirmed;
            if (previousConfirmed <= 0) {
              this.showAtcBtn();
            } else {
              this.updateQtyDisplay();
              this.updateLeftIcon();
              if (!this.qtySelector.classList.contains('pcard-atc--enter')) {
                this.showQtySelector();
              }
            }
          }
        } finally {
          this._syncInFlight = false;
          if (this._syncPending) {
            this._syncPending = false;
            this._syncToServer();
          }
        }
      };

      _showErrorTooltip = (message) => {
        window.FoxTheme.showErrorTooltip(this, message);
      };

      // After a server rejection: refetch /cart.js, sync local state, and
      // broadcast cart-update so every other pcard-atc for the same variant
      // (cards, sticky bar, quickview) lands on the authoritative quantity.
      _resyncFromCart = async () => {
        try {
          invalidateCartCache();
          const cart = await fetchCartData();
          const item = (cart.items || []).find(
            (i) => String(i.variant_id) === String(this.variantId)
          );
          const realQty = item ? item.quantity : 0;
          this.qty = realQty;
          this._confirmedQty = realQty;
          if (realQty > 0) {
            this.updateQtyDisplay();
            this.updateLeftIcon();
            this.showQtySelector();
          } else {
            this.showAtcBtn();
          }
          window.FoxTheme.pubsub.publish(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            { cart }
          );
        } catch (e) {
          console.error('resync failed', e);
        }
      };

      _publishCartUpdate = async () => {
        invalidateCartCache();
        try {
          const cartJson = await fetchCartData();
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: cartJson,
          });
        } catch (err) {
          console.error(err);
        }
      };

      _detectImageBrightness = () => {
        const card = this.closest('[class*="product-card"], .card');
        const img = card && card.querySelector('img');
        if (!img) return;

        const sample = () => {
          try {
            // Sample the bottom 30% of the image — where the selector sits
            _samplerCtx.drawImage(
              img,
              0, img.naturalHeight * 0.7,
              img.naturalWidth, img.naturalHeight * 0.3,
              0, 0, 40, 20
            );
            const data = _samplerCtx.getImageData(0, 0, 40, 20).data;
            let luminance = 0;
            for (let i = 0; i < data.length; i += 4) {
              luminance += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            }
            const avg = luminance / (data.length / 4);
            this.classList.add(avg < 140 ? 'pcard-atc--on-dark' : 'pcard-atc--on-light');
          } catch (_) {
            // Cross-origin image — fall back to light styling
            this.classList.add('pcard-atc--on-light');
          }
        };

        if (img.complete && img.naturalWidth > 0) {
          sample();
        } else {
          img.addEventListener('load', sample, { once: true });
        }
      };

      showQtySelector = () => {
        this.qtySelector.setAttribute('aria-hidden', 'false');
        this.qtyMinus.removeAttribute('tabindex');
        this.qtyPlus.removeAttribute('tabindex');
        this.atcBtn.classList.add('pcard-atc--exit');
        this.qtySelector.classList.add('pcard-atc--enter');
      };

      showAtcBtn = () => {
        this.qtySelector.setAttribute('aria-hidden', 'true');
        this.qtyMinus.setAttribute('tabindex', '-1');
        this.qtyPlus.setAttribute('tabindex', '-1');
        this.qtySelector.classList.remove('pcard-atc--enter');
        this.atcBtn.classList.remove('pcard-atc--exit');
      };

      updateQtyDisplay = () => {
        if (this.qtyValue) this.qtyValue.textContent = this.qty;
      };

      // Show trash when qty === 1 (one tap removes), minus otherwise
      updateLeftIcon = () => {
        if (!this.iconMinus || !this.iconTrash) return;
        const isOne = this.qty <= 1;
        this.iconMinus.hidden = isOne;
        this.iconTrash.hidden = !isOne;
      };
    }

    customElements.define('pcard-atc', PcardAtc);
  }
})();
